import React, { useState } from "react";
import { toast } from "react-toastify";
import Card from "../../components/Card";
import Table from "../../components/Table";
import EditClientModal from "./Components/EditClientModal";
import BulkLedgerModal from "./Components/BulkLedgerModal";
import ValidateLedgerModal from "./Components/ValidateLedgerModal";
import LedgerStatusModal from "./Components/LedgerStatusModal";
import AddNewLedgerUpload from "./Components/AddNewLedgerUpload";
import Pagination from "../../components/Pagination";
import {
  documentStatusOptions,
  approveStatusOptions,
  msmeStatusOptions,
  entityCategoryOptions,
  getEntityListColumns,
  useEntityList,
} from "./TableData";
import {
  LOB_OPTIONS_CLIENT,
  LOB_OPTIONS_SERVICE_PARTNER,
  LOB_OPTIONS_ALL,
  AA_DEPT_MAP,
} from "../CreateNew/CreateNewForm/Clients/ClientFields";
import SearchBar from "../../components/SearchBar";
import dataNotFound from "../../assets/images/emptyPageImage.png";
import DropDown from "../../components/DropDown";
import { downloadEntityData } from "../../utils/downloadUtils";
import { downloadTemplateAction } from "../../actions/downloadTemplateAction";
import { usePermissions } from "../../utils/roleUtils";
import "./index.scss";
import { getUserData } from "../../utils/authnicate";

const ClientList = () => {
  const { isFinance } = usePermissions();
  const canSelect = isFinance; // Only Finance team can select entities

  // ── Selection state (Finance only) ────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkLedgerOpen, setBulkLedgerOpen] = useState(false);
  const [bulkLedgerOpenNew, setBulkLedgerOpenNew] = useState(false);

  // ── Ledger status modal (Finance — verified entities) ─────────────────────
  const [ledgerStatusOpen, setLedgerStatusOpen] = useState(false);
  const [ledgerStatusRow, setLedgerStatusRow] = useState(null);

  const handleLedgerClick = (row) => {
    setLedgerStatusRow(row);
    setLedgerStatusOpen(true);
  };

  // ── Add New Ledger modal (Finance) ────────────────────────────────────────
  const [addNewLedgerOpen, setAddNewLedgerOpen] = useState(false);
  const [addNewLedgerRow, setAddNewLedgerRow] = useState(null);

  const handleAddLedgerClick = (row) => {
    setAddNewLedgerRow(row);
    setAddNewLedgerOpen(true);
  };

  const getRowId = (row) =>
    row.entity?.entityPrimaryId || row.entity?.id || row.entity?.entityId;

  const handleSelectRow = (id, row, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
    setSelectedRows((prev) =>
      checked ? [...prev, row] : prev.filter((r) => getRowId(r) !== id),
    );
  };

  const handleSelectAll = (checked, data) => {
    if (checked) {
      const newIds = new Set(data.map(getRowId));
      setSelectedIds(newIds);
      setSelectedRows(data);
    } else {
      setSelectedIds(new Set());
      setSelectedRows([]);
    }
  };

  // Reset selection after bulk ledger upload
  const handleResetSelection = () => {
    setSelectedIds(new Set());
    setSelectedRows([]);
  };

  // Download template for selected entities via API (returns xlsx)
  const handleDownloadSelected = async () => {
    if (selectedRows.length === 0) return;
    const ids = selectedRows
      .map((row) => row.entity?.entityPrimaryId || row.entity?.id)
      .filter(Boolean);
    const result = await downloadTemplateAction(ids);
    if (!result.success) {
      toast.error(result.message || "Failed to download template");
    }
  };

  const loggedInUser = getUserData();

  const defCreatedByLoggedEmail = loggedInUser.email;

  // ── Dashboard counts for stat cards ──────────────────────────────────────
  // const { counts, isLoading: countsLoading } = useDashboardCounts();

  const {
    isEditModalOpen,
    selectedClient,
    isLoading,
    handleEdit,
    handleSaveClient,
    handleApproveClient,
    handleDeficiencyClient,
    handleCloseModal,
    handleDelete,
    handleResendClient,
    currentEntities,
    pageSize,
    handleClearFilter,
    pagination,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    entityCategory,
    setEntityCategory,
    documentStatus,
    setDocumentStatus,
    approveStatus,
    setApproveStatus,
    lobType,
    setLobType,
    aaDepartmentFunction,
    setAaDepartmentFunction,
    msmeStatus,
    setMsmeStatus,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    dateError,
  } = useEntityList();

  // ── Dynamic LOB + Dept options based on selected category/LOB ────────────
  const resolvedLobOptions = (() => {
    const cat = (entityCategory || "").toLowerCase().trim();
    if (cat === "client") {
      return [{ value: "", label: "LOB Type" }, ...LOB_OPTIONS_CLIENT.slice(1)];
    }
    if (cat === "service partner") {
      return [
        { value: "", label: "LOB Type" },
        ...LOB_OPTIONS_SERVICE_PARTNER.slice(1),
      ];
    }
    return [{ value: "", label: "LOB Type" }, ...LOB_OPTIONS_ALL.slice(1)];
  })();

  const resolvedDeptOptions = (() => {
    const cat = (entityCategory || "").toLowerCase().trim();
    const lob = (lobType || "").toLowerCase().trim();
    const deptMap = AA_DEPT_MAP[cat] || AA_DEPT_MAP[entityCategory] || {};
    const opts = deptMap[lob];
    if (opts && opts.length > 1) {
      return [{ value: "", label: "AA Department" }, ...opts.slice(1)];
    }
    // Flatten all dept options for the category when no LOB selected
    if (!lob) {
      const allOpts = Object.values(deptMap).flat();
      const unique = [
        { value: "", label: "AA Department" },
        ...Array.from(
          new Map(
            allOpts.filter((o) => o.value).map((o) => [o.value, o]),
          ).values(),
        ),
      ];
      return unique.length > 1
        ? unique
        : [{ value: "", label: "AA Department" }];
    }
    return [{ value: "", label: "AA Department" }];
  })();

  return (
    <div className="client-list-page">
      <div className="page-header">
        <h2 className="page-title">All Clients / Service Partner</h2>

        {/* Download Selected — shown whenever any row is checked */}
        {selectedIds.size > 0 && (
          <button
            className="button-selected-data-download"
            onClick={handleDownloadSelected}
            title={`Download ${selectedIds.size} selected entities`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Selected
            <span className="button-selected-data-download__badge">
              {selectedIds.size}
            </span>
          </button>
        )}

        {isFinance && (
          <>
            {/* <button
              className="btn-upload-ledger"
              disabled={selectedIds.size === 0}
              onClick={() => setBulkLedgerOpen(true)}
              title={
                selectedIds.size === 0
                  ? "Select at least one entity"
                  : `Upload ledger for ${selectedIds.size} selected`
              }
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              Upload Ledger File
              {selectedIds.size > 0 && (
                <span className="btn-upload-ledger__badge">
                  {selectedIds.size}
                </span>
              )}
            </button> */}

            <button
              className="btn-upload-ledger"
              // disabled={selectedIds.size === 0}
              onClick={() => setBulkLedgerOpenNew(true)}
              title={
                selectedIds.size === 0
                  ? "Select at least one entity"
                  : `Validate ledger for ${selectedIds.size} selected`
              }
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              Validate Ledger Template
              {/* {selectedIds.size > 0 && (
                <span className="btn-upload-ledger__badge">
                  {selectedIds.size}
                </span>
              )} */}
            </button>
          </>
        )}
      </div>

      {/* ── Filter bar: 2 rows ── */}
      <div className="filter-container-box">
        {/* Row 1: search (80%) + download button (20%) */}
        <div className="filter-row filter-row--top">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by entity name, email..."
          />
          <button
            className="btn-download"
            title="Download data"
            onClick={() =>
              downloadEntityData({
                entityCategory: entityCategory || undefined,
                lobType: lobType || undefined,
                isVerified:
                  approveStatus === "true"
                    ? true
                    : approveStatus === "false"
                      ? false
                      : null,
                entityName:
                  searchQuery && !searchQuery.includes("@")
                    ? searchQuery
                    : undefined,
                emailAddress:
                  searchQuery && searchQuery.includes("@")
                    ? searchQuery
                    : undefined,
              })
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>
        </div>

        {/* Row 2: first 4 filters */}
        <div className="filter-row filter-row--bottom">
          <DropDown
            name="entityCategory"
            value={entityCategory}
            onChange={(e) => {
              setEntityCategory(e.target.value);
              setLobType(""); // reset LOB when category changes
              setAaDepartmentFunction(""); // reset dept too
            }}
            options={entityCategoryOptions}
          />
          <DropDown
            name="lobType"
            value={lobType}
            onChange={(e) => setLobType(e.target.value)}
            options={resolvedLobOptions}
          />
          <DropDown
            name="aaDepartmentFunction"
            value={aaDepartmentFunction}
            onChange={(e) => setAaDepartmentFunction(e.target.value)}
            options={resolvedDeptOptions}
          />
          <DropDown
            name="approveStatus"
            value={approveStatus}
            onChange={(e) => setApproveStatus(e.target.value)}
            options={approveStatusOptions}
            disabled={isFinance}
          />
        </div>

        {/* Row 3: remaining filters + date range */}
        <div className="filter-row filter-row--bottom filter-row--bottom-3">
          <DropDown
            name="msmeStatus"
            value={msmeStatus}
            onChange={(e) => setMsmeStatus(e.target.value)}
            options={msmeStatusOptions}
          />
          <DropDown
            name="documentStatus"
            value={documentStatus}
            onChange={(e) => setDocumentStatus(e.target.value)}
            options={documentStatusOptions}
          />
          <div className="date-range-filter date-range-filter--wide">
            <input
              type="date"
              className={`date-input${dateError ? " date-input--error" : ""}`}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              title="From date"
            />
            <span className="date-range-filter__sep">—</span>
            <input
              type="date"
              className={`date-input${dateError ? " date-input--error" : ""}`}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              title="To date"
            />
            {dateError && (
              <span className="date-range-filter__error">{dateError}</span>
            )}
          </div>
        </div>

        {/* Row 3: clear button — left aligned */}
        <div className="filter-row filter-row--actions">
          <button onClick={handleClearFilter} className="clear-filter-text">
            Clear filters
          </button>
        </div>
      </div>

      <Card>
        {isLoading ? (
          <div className="loading-state">
            <p>Loading entities...</p>
          </div>
        ) : currentEntities.length === 0 ? (
          <div className="empty-state">
            <img
              src={dataNotFound}
              className="data-not-found-img"
              alt="data not found"
            />
          </div>
        ) : (
          <div className="entity-table-wrapper">
            <Table
              columns={getEntityListColumns(isFinance)}
              data={currentEntities}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onResend={handleResendClient}
              onLedger={handleLedgerClick}
              onAddLedger={handleAddLedgerClick}
              showPagination={false}
              selectable={canSelect}
              selectedIds={selectedIds}
              onSelectRow={handleSelectRow}
              onSelectAll={handleSelectAll}
              getRowId={getRowId}
            />
            <Pagination
              currentPage={pagination.currentPage || 1}
              totalPages={pagination.totalPages || 1}
              totalItems={pagination.totalItems || 0}
              itemsPerPage={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
              showInfo={true}
            />
          </div>
        )}
      </Card>

      <EditClientModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        clientData={selectedClient}
        onSave={handleSaveClient}
        onApprove={handleApproveClient}
        defCreatedBy={defCreatedByLoggedEmail}
        onDeficiency={handleDeficiencyClient}
      />

      {isFinance && (
        <BulkLedgerModal
          isOpen={bulkLedgerOpen}
          onClose={() => setBulkLedgerOpen(false)}
          selectedEntities={selectedRows}
          onSuccess={handleResetSelection}
        />
      )}
      {isFinance && (
        <ValidateLedgerModal
          isOpen={bulkLedgerOpenNew}
          onClose={() => setBulkLedgerOpenNew(false)}
          selectedEntities={selectedRows}
          onSuccess={handleResetSelection}
        />
      )}

      <LedgerStatusModal
        isOpen={ledgerStatusOpen}
        onClose={() => {
          setLedgerStatusOpen(false);
          setLedgerStatusRow(null);
        }}
        entityRow={ledgerStatusRow}
      />

      <AddNewLedgerUpload
        isOpen={addNewLedgerOpen}
        onClose={() => {
          setAddNewLedgerOpen(false);
          setAddNewLedgerRow(null);
        }}
        entityRow={addNewLedgerRow}
      />
    </div>
  );
};

export default ClientList;
